import React, { useState } from 'react';
import { Languages, Sparkles, Loader2 } from 'lucide-react';
import { adminApi } from '../../services/api';
import { useToast } from '../../hooks/use-toast';

/**
 * I18nInput: edits a localized field { es, en }.
 * - shows ES/EN tabs
 * - has a "Translate from ES" button (one-shot) that calls /api/admin/translate
 */
export const I18nInput = ({ value, onChange, label, multiline = false, rows = 3, placeholder = '' }) => {
  const [tab, setTab] = useState('es');
  const [translating, setTranslating] = useState(false);
  const { toast } = useToast();
  const v = normalize(value);

  const set = (lang, text) => onChange({ ...v, [lang]: text });

  const translateNow = async () => {
    if (!v.es) {
      toast({ title: 'Falta el texto en español', description: 'Escribe primero el contenido en ES para traducirlo.' });
      return;
    }
    setTranslating(true);
    try {
      const { data } = await adminApi.post('/admin/translate', {
        texts: [v.es],
        source: 'es',
        target: 'en',
      });
      const out = data?.translations?.[0] || '';
      onChange({ ...v, en: out });
      setTab('en');
      toast({ title: 'Traducido', description: 'Revisa la versión inglesa.' });
    } catch (e) {
      toast({ title: 'Error de traducción', description: e.response?.data?.detail || 'Inténtalo de nuevo.' });
    } finally {
      setTranslating(false);
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-2 gap-3">
        {label ? (
          <span className="text-[11px] uppercase tracking-widest text-white/50">{label}</span>
        ) : (
          <span />
        )}
        <div className="flex items-center gap-2">
          <TabBtn active={tab === 'es'} onClick={() => setTab('es')} flag="ES" />
          <TabBtn active={tab === 'en'} onClick={() => setTab('en')} flag="EN" />
          <button
            type="button"
            onClick={translateNow}
            disabled={translating}
            className="inline-flex items-center gap-1 px-2 h-6 rounded-md text-[10px] uppercase tracking-widest bg-[#00e0c7]/10 border border-[#00e0c7]/30 text-[#9be8dc] hover:bg-[#00e0c7]/20 disabled:opacity-50 transition-colors"
            title="Traducir ES → EN con IA"
          >
            {translating ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />}
            IA
          </button>
        </div>
      </div>
      {multiline ? (
        <textarea
          rows={rows}
          className="ldc-input resize-none"
          placeholder={placeholder}
          value={v[tab] || ''}
          onChange={(e) => set(tab, e.target.value)}
        />
      ) : (
        <input
          className="ldc-input"
          placeholder={placeholder}
          value={v[tab] || ''}
          onChange={(e) => set(tab, e.target.value)}
        />
      )}
    </div>
  );
};

const TabBtn = ({ active, onClick, flag }) => (
  <button
    type="button"
    onClick={onClick}
    className={`px-2 h-6 rounded-md text-[10px] uppercase tracking-widest font-medium transition-colors ${
      active
        ? 'bg-white/10 text-white border border-white/15'
        : 'text-white/40 hover:text-white border border-transparent'
    }`}
  >
    {flag}
  </button>
);

const normalize = (v) => {
  if (v === null || v === undefined) return { es: '', en: '' };
  if (typeof v === 'string') return { es: v, en: '' };
  return { es: v.es || '', en: v.en || '' };
};

/** Bulk translate helper for any object of localized strings */
export async function bulkTranslate(items, toastFn) {
  // items: array of { value: {es,en}, set: (newValue) => void }
  const toTranslate = items.filter((it) => it.value?.es && !it.value?.en);
  if (toTranslate.length === 0) {
    toastFn?.({ title: 'Nada que traducir', description: 'Todos los campos ya tienen versión EN.' });
    return;
  }
  const texts = toTranslate.map((it) => it.value.es);
  try {
    const { data } = await adminApi.post('/admin/translate', { texts, source: 'es', target: 'en' });
    const out = data?.translations || [];
    toTranslate.forEach((it, i) => it.set({ ...it.value, en: out[i] || '' }));
    toastFn?.({ title: `${out.length} textos traducidos` });
  } catch (e) {
    toastFn?.({ title: 'Error de traducción', description: e.response?.data?.detail || 'Inténtalo de nuevo' });
  }
}

export default I18nInput;
